import { createSlice } from '@reduxjs/toolkit'
import { orderByPosition } from 'apps/builder/store/helpers'
import {
  updateEntriesPosition,
  adjustIndexesAndReferences,
  filterDeletedEntry,
} from 'apps/builder/store/reducers/entities/entries'
import { reIndexOnDelete } from 'apps/formsCreationKit/lib/utils'
import {
  entriesSliceExtraReducers as entriesSliceBaseExtraReducers,
  entriesSliceConfig as entriesSliceBaseConfig,
  createTableKeyfield,
} from 'apps/formsCreationKit/store/reducers/entities/entries'
import {
  addPdf,
  resetPdf,
  createElement,
  deleteElement,
  updateElement,
  replacePdf,
  createTable,
  deleteTable,
  addTableColumn,
  deleteTableColumn,
} from 'apps/studio/store/actions/sharedActions'

// Replace then restore position order.
const replaceSheetEntries = (state, { payload: { entries, sheetIds } }) =>
  entries && sheetIds
    ? [
        ...state.filter(e => !sheetIds.includes(e.sheet_id)),
        ...entries,
      ].sort(orderByPosition)
    : state

export const entriesSliceStudioExtraReducers = builder => {
  builder
    .addCase(createElement, (state, action) => {
      const { entry } = action.payload
      const { sheet_id } = entry
      const sheetEntriesCount = state.filter(
        entry => entry.sheet_id === sheet_id
      ).length
      // Always append at the end of the sheet regardless of what position was
      // passed in. handleDataInputDrop uses PDF element count as a proxy for
      // sheet entry count; the two can diverge after moves/deletes and produce
      // duplicate positions that break Form Outline drag-and-drop.
      const normalizedEntry = { ...entry, position: sheetEntriesCount }
      return [...state, normalizedEntry]
        .map(updateEntriesPosition(normalizedEntry, sheetEntriesCount))
        .sort(orderByPosition)
    })
    .addCase(createTable, createTableKeyfield)
    .addCase(deleteTable, (state, action) => {
      const { keyFieldSheet, tableSheet } = action.payload
      const sheetIds = new Set([keyFieldSheet.id, tableSheet.id])
      return state.filter(entry => !sheetIds.has(entry.sheet_id))
    })
    .addCase(addTableColumn, replaceSheetEntries)
    .addCase(deleteTableColumn, replaceSheetEntries)
    .addCase(deleteElement, (state, action) => {
      const { entry: entryToDelete } = action.payload
      const { guid } = entryToDelete
      const { position, sheet_id } = state.find(entry => entry.guid === guid)
      const newState = filterDeletedEntry(state, guid)
      return reIndexOnDelete(newState, position, sheet_id)
    })
    .addCase(updateElement, (state, action) => {
      const { entry } = action.payload
      const {
        guid: update_guid,
        sheet_id: update_sheet_id,
      } = entry
      const { sheet_id: old_sheet_id, position: old_position } = state.find(
        entry => entry.guid === update_guid
      )

      // No changes need to be made to the entry unless the sheet has changed
      if (update_sheet_id === old_sheet_id) return state

      // Use the target sheet's current entry count so the moved entry always
      // appends at the end with a sequential position, regardless of what
      // position the PDF element count suggested.
      const targetSheetEntriesCount = state.filter(
        e => e.sheet_id === update_sheet_id
      ).length

      const changes = {
        guid: update_guid,
        sheet: update_sheet_id,
        oldSheet: old_sheet_id,
        newPosition: targetSheetEntriesCount,
        oldPosition: old_position,
      }
      return state
        .map(adjustIndexesAndReferences(changes))
        .sort(orderByPosition)
    })
    .addCase(addPdf, () => [])
    .addCase(resetPdf, () => [])
    .addCase(replacePdf, (state, action) => {
      const { removedEntryGuids } = action.payload
      return removedEntryGuids.reduce((currentState, guid) => {
        const entry = currentState.find(e => e.guid === guid)
        if (!entry) return currentState
        const { position, sheet_id } = entry
        const filtered = filterDeletedEntry(currentState, guid)
        return reIndexOnDelete(filtered, position, sheet_id)
      }, state)
    })
}

export const entriesSliceStudioConfig = {
  ...entriesSliceBaseConfig,
  extraReducers: builder => {
    entriesSliceBaseExtraReducers(builder)
    entriesSliceStudioExtraReducers(builder)
  },
}

export const entriesSlice = createSlice(entriesSliceStudioConfig)
export const { actions: entriesActions, reducer: entriesReducer } = entriesSlice
